import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import { useFacilityStore } from '../../../../../app/z-store/store';
import {
  getCommonAreaGeneratorReadingsUrl,
  getCommonAreaWaterReadingsUrl,
  getCommonAreaElectricityReadingsUrl
} from '../../../../../utils/urls';

const DashboardTab = () => {
  // Updated to match WaterTab and ElectricityTab implementation
  const facilityId = useFacilityStore((state) => state.facilityId);
  const [loading, setLoading] = useState(false);
  const [dashboardStartDate, setDashboardStartDate] = useState('');
  const [dashboardEndDate, setDashboardEndDate] = useState('');
  const [dashboardData, setDashboardData] = useState({
    currentDate: new Date().toLocaleDateString('en-GB'),
    totalWaterConsumption: 0,
    totalElectricityConsumption: 0,
    totalGeneratorFuel: 0,
    totalGeneratorHours: 0,
    monthlyData: [],
    rawData: {
      generator: [],
      water: [],
      electricity: []
    }
  });

  useEffect(() => {
    if (facilityId) {
      fetchDashboardData();
    }
  }, [facilityId]);

  // Function to fetch dashboard data
  const fetchDashboardData = async () => {
    if (!facilityId) return;

    setLoading(true);
    try {
      // Get current month if no dates are selected
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();

      // Default dates for entire current month
      const defaultStartDate = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-01`;
      const lastDay = new Date(currentYear, currentMonth, 0).getDate();
      const defaultEndDate = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-${lastDay}`;

      // Fetch generator data
      const generatorResponse = await makeRequest2(
        `${getCommonAreaGeneratorReadingsUrl}/${facilityId}`,
        'GET'
      );

      // Fetch water data
      const waterResponse = await makeRequest2(
        `${getCommonAreaWaterReadingsUrl}/${facilityId}`,
        'GET'
      );

      // Fetch electricity data
      const electricityResponse = await makeRequest2(
        `${getCommonAreaElectricityReadingsUrl}/${facilityId}`,
        'GET'
      );

      // Get raw data from responses
      const generatorReadings = generatorResponse?.data?.readings || [];
      const waterReadings = waterResponse?.data?.readings || [];
      const electricityReadings = electricityResponse?.data?.readings || [];

      // Store all raw data
      const rawData = {
        generator: generatorReadings,
        water: waterReadings,
        electricity: electricityReadings
      };

      // Filter data by date range
      const startDate = dashboardStartDate || defaultStartDate;
      const endDate = dashboardEndDate || defaultEndDate;

      filterDashboardData(rawData, startDate, endDate);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toastify("Failed to load dashboard data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Function to filter dashboard data by date range
  const filterDashboardData = (rawData, startDate, endDate) => {
    // Convert date strings to Date objects for comparison
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999); // Set to end of day

    // Filter readings by date range
    const filteredGenerator = rawData.generator.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate >= start && itemDate <= end;
    });

    const filteredWater = rawData.water.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate >= start && itemDate <= end;
    });

    const filteredElectricity = rawData.electricity.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate >= start && itemDate <= end;
    });

    // Calculate totals from filtered data
    const totalWaterConsumption = filteredWater.reduce(
      (sum, item) => sum + (parseFloat(item.consumptionM3) || 0),
      0
    );

    const totalElectricityConsumption = filteredElectricity.reduce(
      (sum, item) => sum + (parseFloat(item.consumptionKWh) || 0),
      0
    );

    const totalGeneratorHours = filteredGenerator.reduce(
      (sum, item) => sum + (parseFloat(item.totalHrsRun) || 0),
      0
    );

    const totalFuelLevel = filteredGenerator.length > 0
      ? filteredGenerator[filteredGenerator.length - 1].totalFuelLevel
      : 'N/A';

    // Create daily data for chart
    const startDay = new Date(start);
    const endDay = new Date(end);
    const monthlyData = [];

    // Loop through each day in the range
    for (let day = new Date(startDay); day <= endDay; day.setDate(day.getDate() + 1)) {
      const currentDay = new Date(day);
      const dateStr = `${currentDay.getDate().toString().padStart(2, '0')}/${(currentDay.getMonth() + 1).toString().padStart(2, '0')}/${currentDay.getFullYear()}`;
      const dateFormatted = currentDay.toISOString().split('T')[0];

      // Find readings for this day
      const dayWaterReadings = filteredWater.filter(item =>
        new Date(item.date).toISOString().split('T')[0] === dateFormatted
      );

      const dayElectricityReadings = filteredElectricity.filter(item =>
        new Date(item.date).toISOString().split('T')[0] === dateFormatted
      );

      const dayGeneratorReadings = filteredGenerator.filter(item =>
        new Date(item.date).toISOString().split('T')[0] === dateFormatted
      );

      // Calculate consumption for the day
      const waterConsumption = dayWaterReadings.reduce(
        (sum, item) => sum + (parseFloat(item.consumptionM3) || 0),
        0
      );

      const electricityConsumption = dayElectricityReadings.reduce(
        (sum, item) => sum + (parseFloat(item.consumptionKWh) || 0),
        0
      );

      const generatorRunHours = dayGeneratorReadings.reduce(
        (sum, item) => sum + (parseFloat(item.totalHrsRun) || 0),
        0
      );

      // Add data point for this day
      monthlyData.push({
        date: dateStr,
        waterConsumption,
        electricityConsumption,
        generatorRunHours
      });
    }

    // Update dashboard data state
    setDashboardData({
      currentDate: new Date().toLocaleDateString('en-GB'),
      totalWaterConsumption,
      totalElectricityConsumption,
      totalGeneratorFuel: totalFuelLevel,
      totalGeneratorHours,
      monthlyData,
      rawData // Store raw data for future filtering
    });
  };

  return (
    <div>
      {/* Date Range Filter */}
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body d-flex align-items-center">
              <div className="me-3">
                <label className="form-label me-2">Start Date:</label>
                <InputText
                  type="date"
                  value={dashboardStartDate}
                  onChange={(e) => setDashboardStartDate(e.target.value)}
                />
              </div>
              <div className="me-3">
                <label className="form-label me-2">End Date:</label>
                <InputText
                  type="date"
                  value={dashboardEndDate}
                  onChange={(e) => setDashboardEndDate(e.target.value)}
                />
              </div>
              <Button
                label="Apply Filters"
                icon="ti ti-filter"
                className="btn btn-primary"
                onClick={() => {
                  if (dashboardData.rawData) {
                    filterDashboardData(
                      dashboardData.rawData,
                      dashboardStartDate,
                      dashboardEndDate
                    );
                  } else {
                    fetchDashboardData();
                  }
                }}
              />
              <Button
                label="Reset"
                icon="ti ti-refresh"
                className="btn btn-secondary ms-2"
                onClick={() => {
                  setDashboardStartDate('');
                  setDashboardEndDate('');
                  if (dashboardData.rawData) {
                    // Get current month dates for reset
                    const currentDate = new Date();
                    const currentMonth = currentDate.getMonth() + 1;
                    const currentYear = currentDate.getFullYear();
                    const startDate = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-01`;
                    const lastDay = new Date(currentYear, currentMonth, 0).getDate();
                    const endDate = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-${lastDay}`;

                    filterDashboardData(dashboardData.rawData, startDate, endDate);
                  } else {
                    fetchDashboardData();
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Summary Statistics in Cards */}
      <div className="row">
        <div className="col-md-3">
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="text-primary">Current Date</h6>
              <h3>{dashboardData.currentDate}</h3>
              <p className="text-muted mb-0">DD/MM/YYYY</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="text-info">Total Water Consumption</h6>
              <h3>{dashboardData.totalWaterConsumption.toFixed(2)} m³</h3>
              <p className="text-muted mb-0">Current month usage</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="text-success">Total Electricity Consumption</h6>
              <h3>{dashboardData.totalElectricityConsumption.toFixed(2)} kWh</h3>
              <p className="text-muted mb-0">Current month usage</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="text-danger">Total Generator Hours</h6>
              <h3>{dashboardData.totalGeneratorHours?.toFixed(2) || 'N/A'}</h3>
              <p className="text-muted mb-0">Current month usage</p>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Line Graph */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5>Monthly Consumption Data</h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : dashboardData.monthlyData && dashboardData.monthlyData.length > 0 ? (
                <div style={{ width: '100%', height: 400 }}>
                  <ResponsiveContainer>
                    <LineChart
                      data={dashboardData.monthlyData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" orientation="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="waterConsumption"
                        name="Water (m³)"
                        stroke="#8884d8"
                        activeDot={{ r: 8 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="electricityConsumption"
                        name="Electricity (kWh)"
                        stroke="#82ca9d"
                        activeDot={{ r: 8 }}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="generatorRunHours"
                        name="Generator (hrs)"
                        stroke="#ff7300"
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="alert alert-info">No data available for the current month</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;